import type { LayoutDocument, LayoutRegion } from './types.js';

type Rect = LayoutRegion['rect'];

/** What a pointer drag on the editor canvas is doing to a region: moving the
 * whole rect, or pulling one of its eight resize handles. */
export type DragMode = 'move' | 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export const RESIZE_HANDLES: Exclude<DragMode, 'move'>[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

/** Smallest size a drag may shrink a region to, in canvas pixels. */
const MIN_SIZE_PX = 8;

/**
 * Applies a drag delta to a region rect and returns the new rect, kept inside
 * the canvas and no smaller than the minimum size.
 *
 * `dx`/`dy` are in layout canvas pixels (the caller has already divided out the
 * preview scale); they are converted to the rect's own unit. px results are
 * rounded to whole pixels and percent results to 0.1%.
 */
export function dragRect(
  start: Rect,
  mode: DragMode,
  dx: number,
  dy: number,
  canvas: { width: number; height: number },
): Rect {
  const percent = start.unit === 'percent';
  const maxW = percent ? 100 : canvas.width;
  const maxH = percent ? 100 : canvas.height;
  const ddx = percent ? (dx / Math.max(canvas.width, 1)) * 100 : dx;
  const ddy = percent ? (dy / Math.max(canvas.height, 1)) * 100 : dy;
  const minW = Math.min(percent ? (MIN_SIZE_PX / Math.max(canvas.width, 1)) * 100 : MIN_SIZE_PX, maxW);
  const minH = Math.min(percent ? (MIN_SIZE_PX / Math.max(canvas.height, 1)) * 100 : MIN_SIZE_PX, maxH);
  const round = percent ? (v: number) => Math.round(v * 10) / 10 : Math.round;

  let left = start.x;
  let top = start.y;
  let right = start.x + start.width;
  let bottom = start.y + start.height;

  if (mode === 'move') {
    const x = round(clamp(start.x + ddx, 0, maxW - start.width));
    const y = round(clamp(start.y + ddy, 0, maxH - start.height));
    return { ...start, x, y };
  }

  if (mode.includes('w')) left = clamp(left + ddx, 0, right - minW);
  if (mode.includes('e')) right = clamp(right + ddx, left + minW, maxW);
  if (mode.includes('n')) top = clamp(top + ddy, 0, bottom - minH);
  if (mode.includes('s')) bottom = clamp(bottom + ddy, top + minH, maxH);

  const x = round(left);
  const y = round(top);
  return { ...start, x, y, width: round(right) - x, height: round(bottom) - y };
}

/** Moves an item within one array, or between two arrays. Returns the new
 * source and target arrays (the same array twice when they coincide). */
export function moveItem<T>(
  from: T[],
  fromIndex: number,
  to: T[],
  toIndex: number,
): { from: T[]; to: T[] } {
  const item = from[fromIndex];
  if (item === undefined) return { from, to };
  if (from === to) {
    const next = from.filter((_, i) => i !== fromIndex);
    const at = clamp(toIndex > fromIndex ? toIndex - 1 : toIndex, 0, next.length);
    next.splice(at, 0, item);
    return { from: next, to: next };
  }
  const nextFrom = from.filter((_, i) => i !== fromIndex);
  const nextTo = [...to];
  nextTo.splice(clamp(toIndex, 0, nextTo.length), 0, item);
  return { from: nextFrom, to: nextTo };
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), Math.max(min, max));
}

/** Gives the layout a new canvas size: px regions are scaled to keep their
 * share of the canvas (percent regions already do), and the orientation
 * follows the new shape. */
export function resizeCanvas(doc: LayoutDocument, width: number, height: number): LayoutDocument {
  const sx = width / Math.max(doc.dimensions.width, 1);
  const sy = height / Math.max(doc.dimensions.height, 1);
  const regions = doc.regions.map((r) => {
    if (r.rect.unit !== 'px') return r;
    const x = Math.round(r.rect.x * sx);
    const y = Math.round(r.rect.y * sy);
    const rect = {
      ...r.rect,
      x,
      y,
      width: Math.max(Math.round((r.rect.x + r.rect.width) * sx) - x, 1),
      height: Math.max(Math.round((r.rect.y + r.rect.height) * sy) - y, 1),
    };
    return { ...r, rect };
  });
  return {
    ...doc,
    dimensions: { width, height, orientation: width >= height ? 'landscape' : 'portrait' },
    regions,
  };
}

/** Canvas-pixel positions of the lines a snapped drag lined up with. */
export interface SnapGuides {
  x: number[];
  y: number[];
}

/**
 * Pulls a dragged rect onto nearby alignment lines — the canvas edges and
 * centre, and the edges and centres of the other regions — when one of its
 * moving edges (or, for a move, its edges or centre) is within `threshold`
 * canvas pixels of one. Returns the snapped rect in its own unit, plus the
 * lines it snapped to (in canvas pixels) so the editor can draw guides.
 */
export function snapRect(
  rect: Rect,
  mode: DragMode,
  others: Rect[],
  canvas: { width: number; height: number },
  threshold: number,
): { rect: Rect; guides: SnapGuides } {
  const cw = Math.max(canvas.width, 1);
  const ch = Math.max(canvas.height, 1);
  const toPx = (r: Rect): Box =>
    r.unit === 'percent'
      ? { x: (r.x / 100) * cw, y: (r.y / 100) * ch, width: (r.width / 100) * cw, height: (r.height / 100) * ch }
      : r;
  const lines = (start: number, size: number): number[] => [start, start + size / 2, start + size];
  const boxes = others.map(toPx);
  const xs = [0, cw / 2, cw, ...boxes.flatMap((o) => lines(o.x, o.width))];
  const ys = [0, ch / 2, ch, ...boxes.flatMap((o) => lines(o.y, o.height))];

  let { x, y, width, height } = toPx(rect);
  const guides: SnapGuides = { x: [], y: [] };

  if (mode === 'move') {
    const sx = nearest(lines(x, width), xs, threshold);
    if (sx) {
      x = clamp(x + sx.delta, 0, cw - width);
      guides.x.push(sx.at);
    }
    const sy = nearest(lines(y, height), ys, threshold);
    if (sy) {
      y = clamp(y + sy.delta, 0, ch - height);
      guides.y.push(sy.at);
    }
  } else {
    if (mode.includes('w')) {
      const s = nearest([x], xs, threshold);
      if (s && width - s.delta >= MIN_SIZE_PX) {
        x += s.delta;
        width -= s.delta;
        guides.x.push(s.at);
      }
    }
    if (mode.includes('e')) {
      const s = nearest([x + width], xs, threshold);
      if (s && width + s.delta >= MIN_SIZE_PX) {
        width += s.delta;
        guides.x.push(s.at);
      }
    }
    if (mode.includes('n')) {
      const s = nearest([y], ys, threshold);
      if (s && height - s.delta >= MIN_SIZE_PX) {
        y += s.delta;
        height -= s.delta;
        guides.y.push(s.at);
      }
    }
    if (mode.includes('s')) {
      const s = nearest([y + height], ys, threshold);
      if (s && height + s.delta >= MIN_SIZE_PX) {
        height += s.delta;
        guides.y.push(s.at);
      }
    }
  }

  if (guides.x.length === 0 && guides.y.length === 0) return { rect, guides };
  return { rect: fromPx(rect, { x, y, width, height }, cw, ch), guides };
}

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The smallest shift that puts one of `edges` on one of `targets`, if within `threshold`. */
function nearest(edges: number[], targets: number[], threshold: number): { delta: number; at: number } | null {
  let best: { delta: number; at: number } | null = null;
  for (const e of edges) {
    for (const t of targets) {
      const delta = t - e;
      if (Math.abs(delta) <= threshold && (!best || Math.abs(delta) < Math.abs(best.delta))) best = { delta, at: t };
    }
  }
  return best;
}

/** Converts a canvas-pixel box back into `like`'s unit, rounded the way dragRect rounds. */
function fromPx(like: Rect, box: Box, cw: number, ch: number): Rect {
  const percent = like.unit === 'percent';
  const round = percent ? (v: number) => Math.round(v * 10) / 10 : Math.round;
  const sx = percent ? 100 / cw : 1;
  const sy = percent ? 100 / ch : 1;
  const x = round(box.x * sx);
  const y = round(box.y * sy);
  return {
    ...like,
    x,
    y,
    width: round(round((box.x + box.width) * sx) - x),
    height: round(round((box.y + box.height) * sy) - y),
  };
}

export type Alignment = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom' | 'fill';

/** Lines a region up with the canvas, or stretches it over the whole canvas. */
export function alignRect(rect: Rect, align: Alignment, canvas: { width: number; height: number }): Rect {
  const percent = rect.unit === 'percent';
  const maxW = percent ? 100 : canvas.width;
  const maxH = percent ? 100 : canvas.height;
  const round = percent ? (v: number) => Math.round(v * 10) / 10 : Math.round;
  switch (align) {
    case 'left':
      return { ...rect, x: 0 };
    case 'center':
      return { ...rect, x: round(Math.max(maxW - rect.width, 0) / 2) };
    case 'right':
      return { ...rect, x: round(Math.max(maxW - rect.width, 0)) };
    case 'top':
      return { ...rect, y: 0 };
    case 'middle':
      return { ...rect, y: round(Math.max(maxH - rect.height, 0) / 2) };
    case 'bottom':
      return { ...rect, y: round(Math.max(maxH - rect.height, 0)) };
    case 'fill':
      return { ...rect, x: 0, y: 0, width: maxW, height: maxH };
  }
}

/** Regions bottom-to-top, the way the player stacks them (zIndex, then document order). */
export function stackOrder(regions: LayoutRegion[]): LayoutRegion[] {
  return regions
    .map((r, i) => ({ r, i }))
    .sort((a, b) => (a.r.zIndex ?? 0) - (b.r.zIndex ?? 0) || a.i - b.i)
    .map(({ r }) => r);
}

/**
 * Moves one region a step up or down the stack (or to the very top/bottom),
 * then renumbers every region's zIndex 0..n-1 in the new stacking order.
 * The regions keep their place in the document array.
 */
export function restack(
  regions: LayoutRegion[],
  id: string,
  to: 'forward' | 'backward' | 'front' | 'back',
): LayoutRegion[] {
  const order = stackOrder(regions);
  const from = order.findIndex((r) => r.id === id);
  if (from < 0) return regions;
  const last = order.length - 1;
  const target = to === 'front' ? last : to === 'back' ? 0 : clamp(from + (to === 'forward' ? 1 : -1), 0, last);
  if (target === from) return regions;
  const [moved] = order.splice(from, 1);
  if (moved) order.splice(target, 0, moved);
  const z = new Map(order.map((r, i) => [r.id, i]));
  return regions.map((r) => ({ ...r, zIndex: z.get(r.id) ?? 0 }));
}
